import { useState } from "react"
import { Button } from "@chakra-ui/react"
import { FaDownload } from "react-icons/fa6"
import { nowDateTime } from "@hydrologiq/common/helpers/date"
import { useRestApi } from "@hydrologiq/shared/providers/helpers/useRestApi"
import { ActivityFilters, ActivitySort } from "@hydrologiq/shared/hooks/activities"

interface ActivitiesExportProps {
  filters: ActivityFilters
  sort: ActivitySort | null
  /** Activity grouping for dynamic column headers (Filling, Movement, Fulfilment, Hold, Inspection, Maintenance) */
  grouping?: string
  disabled?: boolean
  variant?: "hydrologiq_primary" | "hydrologiq_secondary" | "hydrologiq_icon"
  size?: "sm" | "md" | "lg"
}

function ActivitiesExport({
  filters,
  sort,
  grouping,
  disabled = false,
  variant = "hydrologiq_secondary",
  size = "sm",
}: ActivitiesExportProps) {
  const [isExporting, setIsExporting] = useState(false)
  const restApi = useRestApi()

  const generateFilename = () => {
    const now = nowDateTime()
    const timestamp = now.format("YYYY-MM-DD_HH-mm-ss")
    const groupingSuffix = grouping && grouping !== "All" ? `_${grouping.toLowerCase()}` : ""
    return `logiq_activities${groupingSuffix}_export_${timestamp}.csv`
  }

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      // Build parameters for the REST endpoint
      const params: Record<string, string | boolean> = {
        download: true,
      }

      // Add filter parameters (only include non-empty values)
      if (filters.status) params.status = filters.status
      if (filters.kind) params.kind = filters.kind
      if (filters.customer) params.customer = filters.customer
      if (filters.site) params.site = filters.site
      if (filters.asset) params.asset = filters.asset
      if (filters.name) params.name = filters.name

      // Add sort parameter
      if (sort?.field && sort?.direction) {
        params.sort = `${sort.field}:${sort.direction}`
      }

      // Add grouping parameter for dynamic column headers
      if (grouping && grouping !== "All") {
        params.grouping = grouping
      }

      // Add filename parameter
      params.filename = generateFilename()

      // Use the REST API provider's download method
      await restApi.download("/orders/activities/export", { params })
    } catch (error) {
      console.error("Export failed:", error)
      // TODO: Show error toast/notification
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant={variant as any}
      size={size}
      onClick={handleExport}
      disabled={disabled || isExporting}
      loading={isExporting}
    >
      <FaDownload />
      {isExporting ? "Exporting..." : "Export CSV"}
    </Button>
  )
}

export default ActivitiesExport





